import { Button, List, ListItem, ListItemText } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import CheckIcon from "@mui/icons-material/Check";
import styles from "./PublicUserCallToAction.module.css";
import unrulyLogo from "/unruly-logo-color-nobg-rect.png";

type PublicUserCallToActionType = "public-event";

interface PublicUserCallToActionProps {
  type: PublicUserCallToActionType;
  header?: React.ReactNode;
  content?: React.ReactNode;
  footer?: React.ReactNode;
}

const PublicUserCallToAction: React.FC<PublicUserCallToActionProps> = ({
  type,
  header,
  content,
  footer,
}) => {
  if (type === "public-event") {
    return (
      <DisplayDefaultForEvent
        type={type}
        header={header}
        content={content}
        footer={footer}
      />
    );
  }
  return (
    <div className={styles["container"]}>
      <div className={styles["header"]}>{header}</div>
      <div className={styles["content"]}>{content}</div>
      <div className={styles["footer"]}>{footer}</div>
    </div>
  );
};

const DisplayDefaultForEvent: React.FC<PublicUserCallToActionProps> = ({
  type,
  header,
  content,
  footer,
}) => {
  if (type !== "public-event") {
    throw new Error("Type must be public-event for the current display");
  }
  const navigate = useNavigate();
  const defaultHeader = header ?? (
    <div className={styles["header"]}>
      <div className={styles["header-left"]}>
        <div className={styles["logo"]}>
          <img src={unrulyLogo} alt="Unruly Logo" />
        </div>
        <div className={styles["header-content"]}>
          <h2>Unlock the full potential of Unruly</h2>
          <p>
            See how global and local events impact your business then take
            action.
          </p>
        </div>
      </div>
      <div className={styles["header-right"]}>
        <Button
          variant="contained"
          color="success"
          onClick={() => {
            navigate("/");
          }}
        >
          Get Started
        </Button>
        <p className={styles["login-text"]}>
          Already have an account? <Link to="/">Login</Link>
        </p>
      </div>
    </div>
  );
  const defaultFooter = footer ?? (
    <div className={styles["footer"]}>
      <div className={styles["footer-left"]}>
        <h3>
          Unruly helps you understand and act on the latest news and events in
          and around your business.
        </h3>
        <List className={styles["footer-list"]}>
          <ListItem className={styles["footer-list-item"]}>
            <CheckIcon />
            <ListItemText primary="Receive the necessary daily briefs of personalized info to stay ahead of the curve" />
          </ListItem>
          <ListItem className={styles["footer-list-item"]}>
            <CheckIcon />
            <ListItemText primary="Generate reports and analyses on what's most important to you" />
          </ListItem>
          <ListItem className={styles["footer-list-item"]}>
            <CheckIcon />
            <ListItemText primary="Save and share event analyses with your team to make informed decisions" />
          </ListItem>
        </List>
      </div>
      <div className={styles["footer-right"]}>
        <Button
          variant="contained"
          color="success"
          onClick={() => {
            navigate("/");
          }}
        >
          Get Started
        </Button>
        <p className={styles["login-text"]}>
          <Link to="/">Sign in</Link> to your account
        </p>
      </div>
    </div>
  );

  return (
    <div className={styles["container"]}>
      <div className={styles["header"]}>{defaultHeader}</div>
      <div className={styles["content"]}>
        {content ?? <div>No content provided</div>}
      </div>
      <div className={styles["footer"]}>{defaultFooter}</div>
    </div>
  );
};

export default PublicUserCallToAction;
